import { Controller } from "stimulus"

// Agent Portal: WhatsApp Integration routing table.
//
// Everything here is presentational: expanding module groups, labelling
// inherited values and exceptions, naming the provider each inherit option
// resolves to, flagging missing templates and guarding unsaved edits. The
// server re-resolves every rule with the same precedence on save, so the page
// works without JavaScript too (each row's provider select carries its own
// rule, and its blank option means inherit).
export default class extends Controller {
  static targets = [
    "form", "defaultSelect", "moduleRow", "messageRow",
    "drawer", "drawerTitle", "drawerId", "drawerCampaignName", "drawerLabel",
    "drawerTemplateName", "drawerTemplateLanguage", "drawerTemplateText", "paramSelect", "mediaSelect",
    "buttonSection", "buttonIndex", "buttonBaseUrl", "buttonTransform",
    "drawerSource", "drawerTags", "drawerAttributes"
  ]

  connect() {
    this.dirty = false
    this.providerLabels = { gallabox: "Gallabox", aisensy: "AiSensy" }
    this.lastDefault = this.defaultProvider()
    this.unloadHandler = (event) => {
      if (!this.dirty) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this.unloadHandler)
    this.render()
    // Any click or change inside a module's rows marks it as the one being
    // worked on (expanding/collapsing is handled by toggleModule itself).
    this.rememberHandler = (event) => {
      if (event.target.closest("[data-action*='toggleModule']")) return
      const row = event.target.closest("tr[data-module]")
      if (row) this.rememberModule(row.dataset.module)
    }
    this.element.addEventListener("click", this.rememberHandler)
    this.element.addEventListener("change", this.rememberHandler)
    // Only the module worked on last opens; every other group starts collapsed.
    const last = this.moduleRow(this.lastModule())
    if (last) this.setExpanded(last, true)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.unloadHandler)
    this.element.removeEventListener("click", this.rememberHandler)
    this.element.removeEventListener("change", this.rememberHandler)
  }

  // ---- last worked module (per client, this browser only) ----------------

  lastModuleKey() {
    return `wa-last-module:${this.element.dataset.whatsappIntegrationClientId}`
  }

  lastModule() {
    try {
      return window.localStorage.getItem(this.lastModuleKey())
    } catch (_error) {
      return null
    }
  }

  rememberModule(moduleName) {
    try {
      if (moduleName) window.localStorage.setItem(this.lastModuleKey(), moduleName)
      else window.localStorage.removeItem(this.lastModuleKey())
    } catch (_error) {
      // Storage unavailable (private window, blocked site data): nothing to remember.
    }
  }

  // ---- state -------------------------------------------------------------

  defaultProvider() {
    const select = this.hasDefaultSelectTarget ? this.defaultSelectTarget : null
    return select && select.value ? select.value : "gallabox"
  }

  // The row's own rule. Blank means inherit from the level above.
  ruleValue(row) {
    const select = row.querySelector("select[data-role=provider]")
    return select ? select.value : ""
  }

  // Message selects have no blank option: showing the organization default
  // means following it, so only a different provider is the message's own rule.
  messageRule(row) {
    const value = this.ruleValue(row)
    return value === this.defaultProvider() ? "" : value
  }

  moduleRow(moduleName) {
    return this.moduleRowTargets.find((row) => row.dataset.module === moduleName)
  }

  messagesFor(moduleName) {
    return this.messageRowTargets.filter((row) => row.dataset.module === moduleName)
  }

  effectiveModule(row) {
    return this.ruleValue(row) || this.defaultProvider()
  }

  effectiveMessage(row) {
    const own = this.ruleValue(row)
    if (own) return own
    const parent = this.moduleRow(row.dataset.module)
    return parent ? this.effectiveModule(parent) : this.defaultProvider()
  }

  // ---- rendering ---------------------------------------------------------

  render() {
    this.moduleRowTargets.forEach((row) => this.renderModule(row))
    this.messageRowTargets.forEach((row) => this.renderMessage(row))
    this.element.classList.toggle("wa-dirty", this.dirty)
  }

  renderModule(row) {
    const own = this.ruleValue(row)
    const effective = this.effectiveModule(row)
    const status = row.querySelector("[data-role=module-status]")
    const children = this.messagesFor(row.dataset.module)
    const exceptions = children.filter((child) => this.effectiveMessage(child) !== effective).length

    row.classList.toggle("wa-explicit", own !== "")
    if (status) {
      let text = own ? `Explicit: ${this.providerLabels[own]}` : `Inherited: ${this.providerLabels[effective]} (organization default)`
      status.className = "wa-badge " + (own ? "wa-badge-explicit" : "")
      if (children.length && exceptions > 0) {
        text += ` · Mixed: ${exceptions} exception${exceptions === 1 ? "" : "s"}`
        status.className = "wa-badge wa-badge-mixed"
      }
      status.textContent = children.length ? text : ""
    }
    this.labelInherit(row, "Use organization default", this.defaultProvider())
  }

  renderMessage(row) {
    const own = this.messageRule(row)
    const effective = this.effectiveMessage(row)
    const status = row.querySelector("[data-role=message-status]")
    const parent = this.moduleRow(row.dataset.module)
    const parentExplicit = parent && this.ruleValue(parent) !== ""
    const isException = own !== "" && parent && own !== this.effectiveModule(parent)

    row.classList.toggle("wa-exception", own !== "")
    if (status) {
      if (own) {
        status.textContent = isException ? `Exception: ${this.providerLabels[own]}` : `Explicit: ${this.providerLabels[own]}`
        status.className = "wa-badge wa-badge-explicit"
      } else {
        status.textContent = `Inherited: ${this.providerLabels[effective]} (${parentExplicit ? "module rule" : "organization default"})`
        status.className = "wa-badge"
      }
    }

    const gallaboxBlock = row.querySelector("[data-role=gallabox-template]")
    const aisensyBlock = row.querySelector("[data-role=aisensy-template]")
    if (gallaboxBlock) gallaboxBlock.classList.toggle("is-hidden", effective !== "gallabox")
    if (aisensyBlock) aisensyBlock.classList.toggle("is-hidden", effective !== "aisensy")

    const select = row.querySelector("select[data-role=campaign-select]")
    const missing = row.querySelector("[data-role=missing]")
    const hasMapping = !!(select && select.value)
    row.classList.toggle("wa-missing-template", effective === "aisensy" && !hasMapping)
    if (missing) missing.classList.toggle("is-hidden", !(effective === "aisensy" && !hasMapping))
  }

  // Keeps the blank option honest: it always shows the provider it resolves
  // to, so a row left alone reads as Gallabox rather than as "nothing chosen".
  labelInherit(row, prefix, resolved) {
    const option = row.querySelector("select[data-role=provider] option[value='']")
    if (option) option.textContent = `${prefix} (${this.providerLabels[resolved] || "Gallabox"})`
  }

  // ---- actions -----------------------------------------------------------

  ruleChanged() {
    // Messages that followed the old organization default move with it.
    const current = this.defaultProvider()
    if (current !== this.lastDefault) {
      this.messageRowTargets.forEach((row) => {
        const select = row.querySelector("select[data-role=provider]")
        if (select && select.value === this.lastDefault) select.value = current
      })
      this.lastDefault = current
    }
    this.dirty = true
    this.render()
  }

  // Provider Accounts: one panel at a time, chosen by its tab.
  selectProviderTab(event) {
    event.preventDefault()
    const tab = event.currentTarget.closest("[data-role=provider-tab]")
    if (tab) this.showProviderPanel(tab.dataset.provider)
  }

  showProviderPanel(provider) {
    this.element.querySelectorAll("[data-role=provider-tab]").forEach((tab) => {
      tab.classList.toggle("is-active", tab.dataset.provider === provider)
    })
    this.element.querySelectorAll("[data-role=provider-panel]").forEach((panel) => {
      panel.classList.toggle("is-hidden", panel.dataset.provider !== provider)
    })
  }

  // The saved API key shows masked and locked. Clear unlocks an empty field;
  // leaving it empty or not saving keeps the saved key on the server.
  clearApiKey(event) {
    const input = this.element.querySelector("[data-role=api-key-input]")
    if (!input) return
    input.disabled = false
    input.value = ""
    input.focus()
    event.currentTarget.classList.add("is-hidden")
  }

  toggleModule(event) {
    const row = event.target.closest("tr")
    const expanded = row.getAttribute("data-expanded") === "true"
    this.setExpanded(row, !expanded)
    if (!expanded) this.rememberModule(row.dataset.module)
    else if (this.lastModule() === row.dataset.module) this.rememberModule(null)
  }

  setExpanded(row, expanded) {
    row.setAttribute("data-expanded", expanded ? "true" : "false")
    const expander = row.querySelector(".wa-expander")
    if (expander) {
      expander.innerHTML = expanded ? "&#9662;" : "&#9656;"
      expander.setAttribute("aria-expanded", expanded ? "true" : "false")
    }
    this.messagesFor(row.dataset.module).forEach((message) => message.classList.toggle("is-hidden", !expanded))
  }

  cancel() {
    if (this.dirty && !window.confirm("Discard the unsaved edits on this page?")) return
    this.dirty = false
    window.location.reload()
  }

  beforeSubmit() {
    this.dirty = false
  }

  // ---- campaign drawer ---------------------------------------------------

  openDrawer(event) {
    const button = event.currentTarget
    const entry = button.dataset.entry ? JSON.parse(button.dataset.entry) : null

    // A campaign is not tied to one message (the row dropdown maps it), so
    // every variable slot is offered; the server checks each mapped message.
    this.drawerTitleTarget.textContent = entry ? `Edit campaign ${entry.campaign_name}` : "Register AiSensy campaign"
    this.applyMessageConstraints(null)
    this.fillDrawer(entry)
    this.drawerTarget.classList.remove("is-hidden")
    this.drawerCampaignNameTarget.focus()
  }

  // Header choice: the URL button fields show only for "Dynamic URL".
  mediaChanged() {
    this.buttonSectionTarget.classList.toggle("is-hidden", this.mediaSelectTarget.value !== "url")
  }

  // Narrow every parameter dropdown to the fields the chosen message supplies.
  // Header options stay generic; the server checks the message can supply the
  // attachment or link on save. Passing null fields restores the full catalog.
  applyMessageConstraints(fields) {
    const allowed = fields ? new Set(fields.map((pair) => pair[0])) : null
    this.paramFieldCount = fields ? fields.length : null
    this.paramSelectTargets.forEach((select) => {
      Array.from(select.options).forEach((option) => {
        if (!option.value) return
        const ok = !allowed || allowed.has(option.value)
        option.hidden = !ok
        option.disabled = !ok
        if (!ok && option.selected) select.value = ""
      })
    })
    this.showParamRows()
  }

  // Show only as many variable rows as the message supplies fields, but never
  // hide a row that holds a value. No message chosen shows all of them.
  showParamRows() {
    const selects = this.paramSelectTargets
    let visible = selects.length
    if (this.paramFieldCount != null) {
      const lastFilled = selects.reduce((last, select, index) => (select.value ? index + 1 : last), 0)
      visible = Math.max(this.paramFieldCount, lastFilled, 1)
    }
    selects.forEach((select, index) => {
      const row = select.closest("[data-role=param-row]")
      if (row) row.classList.toggle("is-hidden", index >= visible)
    })
  }

  fillDrawer(entry) {
    const value = (name) => (entry && entry[name] != null ? entry[name] : "")
    this.drawerIdTarget.value = value("id")
    this.drawerCampaignNameTarget.value = value("campaign_name")
    this.drawerLabelTarget.value = value("label")
    this.drawerTemplateNameTarget.value = value("template_name")
    this.drawerTemplateLanguageTarget.value = value("template_language")
    this.drawerTemplateTextTarget.value = value("template_text")
    const params = entry && Array.isArray(entry.parameters) ? entry.parameters : []
    this.paramSelectTargets.forEach((select, index) => { select.value = params[index] || "" })
    const button = entry && entry.button ? entry.button : null
    this.mediaSelectTarget.value = button ? "url" : value("media") || "none"
    this.mediaChanged()
    this.buttonIndexTarget.value = button ? button.index : "0"
    this.buttonBaseUrlTarget.value = button ? button.base_url : ""
    this.buttonTransformTarget.value = button ? button.transform : "suffix"
    this.drawerSourceTarget.value = value("source")
    this.drawerTagsTarget.value = entry && Array.isArray(entry.tags) ? entry.tags.join(", ") : ""
    const attrs = entry && entry.attributes ? entry.attributes : {}
    this.drawerAttributesTarget.value = Object.keys(attrs).map((name) => `${name}=${attrs[name]}`).join("\n")
    this.showParamRows()
  }

  closeDrawer() {
    this.drawerTarget.classList.add("is-hidden")
  }
}
